import { useUser } from '@/contexts/AuthContext';
import { useLoading } from '@/contexts/LoadingContext';
import { Toast } from '@/lib/Toast';
import { api } from '@/services/apiClient';
import { ICompany } from '@/types/api/companies';
import { handleErrorAsync } from '@/utils/handlers';
import { createInstitutionalPageSchema } from '@/validators/InstitutionalPage';
import { Box } from '@mui/material';
import { Form, Formik } from 'formik';
import CompanyDescription from './CompanyDescription';
import CompanyName from './CompanyName';
import CompanySlug from './CompanySlug';

export enum CreateInstitutionalPageSteps {
  CONFIGURE_COMPANY_NAME = 1,
  CONFIGURE_COMPANY_DESCRIPTION,
  CONFIGURE_COMPANY_SLUG,
}

type InstitutionalPageData = Partial<ICompany>;

interface IViewProps {
  currentStep: CreateInstitutionalPageSteps;
  nextStep: () => void;
  onSuccess: () => void;
}

export const View = ({ currentStep, nextStep, onSuccess }: IViewProps) => {
  const { setIsLoading } = useLoading();
  const { defaultMembership, updateUserData, user } = useUser();

  const initialValues = {
    full_name: defaultMembership?.company.full_name,
    display_name: defaultMembership?.company.display_name || '',
    slogan: defaultMembership?.company.slogan || '',
    description: defaultMembership?.company.description || '',
    slug: defaultMembership?.company.slug || '',
  };

  const handleConfigureInstitutionalPage = async (
    formValues: InstitutionalPageData
  ) => {
    const [companyUpdated, error] = await handleErrorAsync(async () => {
      setIsLoading(true);

      const { data } = await api
        .patch<ICompany>(
          `api/companies/${defaultMembership?.company.id}/`,
          Object.assign({}, formValues, { has_active_institutional_page: true })
        )
        .finally(() => setIsLoading(false));

      return data;
    });

    if (error) return Toast('error', error.message);
    updateUserData({
      memberships: user.memberships?.map(membership =>
        membership.company.id === defaultMembership?.company.id
          ? {
              ...membership,
              company: Object.assign(membership.company, companyUpdated),
            }
          : membership
      ),
    });
    onSuccess();
  };

  return (
    <Formik
      onSubmit={async (values, formikHelpers) => {
        await handleConfigureInstitutionalPage(values);
        formikHelpers.setSubmitting(false);
      }}
      validationSchema={createInstitutionalPageSchema}
      initialValues={initialValues}
      validateOnChange={true}
    >
      {formikProps => (
        <Box
          component={Form}
          sx={theme => ({
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            flexGrow: 1,
            width: '100%',
            color: 'text.secondary',
            margin: 'auto',
            marginTop: 'calc(28vh - 56px)',
            [theme.breakpoints.up('md')]: {
              width: '380px',
            },
          })}
        >
          {
            {
              [CreateInstitutionalPageSteps.CONFIGURE_COMPANY_NAME]: (
                <CompanyName onAdvancedStep={nextStep} />
              ),
              [CreateInstitutionalPageSteps.CONFIGURE_COMPANY_DESCRIPTION]: (
                <CompanyDescription onAdvancedStep={nextStep} />
              ),
              [CreateInstitutionalPageSteps.CONFIGURE_COMPANY_SLUG]: (
                <CompanySlug isSubmitting={formikProps.isSubmitting} />
              ),
            }[currentStep]
          }
        </Box>
      )}
    </Formik>
  );
};

export type { InstitutionalPageData, IViewProps };
